import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGamepad, FaHome, FaSteam, FaBars, FaExclamationTriangle, FaClock, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';

const SteamTierListPage = () => {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [error, setError] = useState(null);

  const STEAM_API_KEY = process.env.REACT_APP_STEAM_API_KEY || 'BC5BAB44723C39C7122F1C3CD338A33B';
  const STEAM_ID = process.env.REACT_APP_STEAM_USER_ID || '76561199466336930';

  const fetchWithProxy = async (url) => {
    try {
      const proxies = [
        'https://corsproxy.io/?',
        'https://api.allorigins.win/raw?url=',
        'https://cors-anywhere.herokuapp.com/'
      ];
      
      for (const proxy of proxies) {
        try {
          const response = await fetch(proxy + encodeURIComponent(url), {
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          });
          
          if (response.ok) {
            return response;
          }
        } catch (proxyError) {
          console.warn(`Proxy ${proxy} failed, trying next...`);
          continue;
        }
      }
      
      throw new Error('All proxies failed');
    } catch (error) {
      console.error('Proxy fetch error:', error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchSteamGames = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const ownedGamesUrl = `https://api.steampowered.com/IPlayerService/GetOwnedGames/v1/?key=${STEAM_API_KEY}&steamid=${STEAM_ID}&include_appinfo=true&include_played_free_games=true`;
        
        const ownedGamesResponse = await fetchWithProxy(ownedGamesUrl);
        
        if (!ownedGamesResponse.ok) {
          throw new Error('Gagal mengambil data game dari Steam API');
        }
        
        const ownedGamesData = await ownedGamesResponse.json();
        
        if (!ownedGamesData.response || !ownedGamesData.response.games) {
          throw new Error('Format data tidak valid dari Steam API');
        }
        
        const ownedGames = ownedGamesData.response.games;

        const gamesWithDetails = await Promise.all(
          ownedGames.slice(0, 20).map(async (game) => {
            try {
              const appDetailsUrl = `https://store.steampowered.com/api/appdetails?appids=${game.appid}`;
              const appDetailsResponse = await fetchWithProxy(appDetailsUrl);
              
              if (!appDetailsResponse.ok) {
                throw new Error('Failed to fetch app details');
              }
              
              const appDetailsData = await appDetailsResponse.json();
              
              if (appDetailsData[game.appid] && appDetailsData[game.appid].success) {
                const data = appDetailsData[game.appid].data;
                
                return {
                  appid: game.appid,
                  name: data.name || 'Unknown Game',
                  header_image: data.header_image || `https://cdn.akamai.steamstatic.com/steam/apps/${game.appid}/header.jpg`,
                  playtime_forever: game.playtime_forever || 0,
                  rtime_last_played: game.rtime_last_played || 0
                };
              } else {
                throw new Error('App details not successful');
              }
            } catch (error) {
              console.warn(`Error fetching details for appid ${game.appid}:`, error);
              return {
                appid: game.appid,
                name: game.name || 'Unknown Game',
                header_image: `https://cdn.akamai.steamstatic.com/steam/apps/${game.appid}/header.jpg`,
                playtime_forever: game.playtime_forever || 0,
                rtime_last_played: game.rtime_last_played || 0
              };
            }
          })
        );
        
        // Urutkan berdasarkan waktu bermain (descending)
        const sortedGames = gamesWithDetails.sort((a, b) => b.playtime_forever - a.playtime_forever);
        setGames(sortedGames);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching Steam games:", error);
        setError(error.message);
        setIsLoading(false);

        // Fallback data
        setTimeout(() => {
          const fallbackGames = [
            {
              appid: 1091500,
              name: "Cyberpunk 2077",
              header_image: "https://cdn.akamai.steamstatic.com/steam/apps/1091500/header.jpg",
              playtime_forever: 85,
              rtime_last_played: Math.floor(Date.now() / 1000) - 86400 * 3
            },
            {
              appid: 1245620,
              name: "ELDEN RING",
              header_image: "https://cdn.akamai.steamstatic.com/steam/apps/1245620/header.jpg",
              playtime_forever: 156,
              rtime_last_played: Math.floor(Date.now() / 1000) - 86400 * 7
            },
            {
              appid: 292030,
              name: "The Witcher 3: Wild Hunt",
              header_image: "https://cdn.akamai.steamstatic.com/steam/apps/292030/header.jpg",
              playtime_forever: 203,
              rtime_last_played: Math.floor(Date.now() / 1000) - 86400 * 15
            },
            {
              appid: 730,
              name: "Counter-Strike 2",
              header_image: "https://cdn.akamai.steamstatic.com/steam/apps/730/header.jpg",
              playtime_forever: 350,
              rtime_last_played: Math.floor(Date.now() / 1000) - 86400
            },
            {
              appid: 578080,
              name: "PUBG: BATTLEGROUNDS",
              header_image: "https://cdn.akamai.steamstatic.com/steam/apps/578080/header.jpg",
              playtime_forever: 120,
              rtime_last_played: Math.floor(Date.now() / 1000) - 86400 * 10
            },
            {
              appid: 1172470,
              name: "Apex Legends",
              header_image: "https://cdn.akamai.steamstatic.com/steam/apps/1172470/header.jpg",
              playtime_forever: 180,
              rtime_last_played: Math.floor(Date.now() / 1000) - 86400 * 2
            },
            {
              appid: 1086940,
              name: "Baldur's Gate 3",
              header_image: "https://cdn.akamai.steamstatic.com/steam/apps/1086940/header.jpg",
              playtime_forever: 95,
              rtime_last_played: Math.floor(Date.now() / 1000) - 86400 * 5
            },
            {
              appid: 271590,
              name: "Grand Theft Auto V",
              header_image: "https://cdn.akamai.steamstatic.com/steam/apps/271590/header.jpg",
              playtime_forever: 210,
              rtime_last_played: Math.floor(Date.now() / 1000) - 86400 * 20
            }
          ];
          
          // Urutkan fallback data juga
          const sortedFallbackGames = fallbackGames.sort((a, b) => b.playtime_forever - a.playtime_forever);
          setGames(sortedFallbackGames);
        }, 500);
      }
    };

    fetchSteamGames();
  }, [STEAM_API_KEY, STEAM_ID]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const formatPlaytime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    return hours >= 1 ? `${hours} jam` : `${minutes} menit`;
  };

  const formatLastPlayed = (timestamp) => {
    if (!timestamp || timestamp === 0) return 'Belum pernah dimainkan';
    
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Hari ini';
    if (diffDays === 1) return 'Kemarin';
    if (diffDays < 7) return `${diffDays} hari lalu`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} minggu lalu`;
    
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (isLoading) {
  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
      <div className="text-center space-y-6">
        {/* Single Pixel Animation */}
        <motion.div
          className="w-4 h-4 bg-red-500 mx-auto pixel-art"
          animate={{
            scale: [1, 1.5, 1],
            boxShadow: [
              '0 0 0px rgba(239, 68, 68, 0)',
              '0 0 20px rgba(239, 68, 68, 0.5)',
              '0 0 0px rgba(239, 68, 68, 0)'
            ]
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <div className="space-y-2">
          <motion.p
            className="text-gray-500 text-xs pixel-font"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            LOADING
          </motion.p>
        </div>
      </div>
    </div>
  );
}

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="bg-gradient-to-r from-gray-800 to-red-600 text-white pixel-border-bottom relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-repeat" style={{ 
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '30px'
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 py-4 relative z-10">
          {/* Bagian atas header dengan logo dan tombol menu */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <motion.div 
                className="mr-3"
                whileHover={{ rotate: 10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <FaSteam className="text-2xl" />
              </motion.div>
              <h1 className="text-2xl md:text-3xl font-bold pixel-font">KOLEKSI GAME STEAM</h1>
            </div>
            
            {/* Tombol menu untuk desktop */}
            <div className="hidden md:flex space-x-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/')}
                className="flex items-center pixel-button bg-red-700 hover:bg-red-800 px-3 py-2 text-sm"
                title="Kembali ke Profil"
              >
                <FaHome className="mr-1" />
                <span>Beranda</span>
              </motion.button>
            </div>

            {/* Tombol burger menu untuk mobile */}
            <div className="md:hidden">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleMobileMenu}
                className="pixel-button bg-red-700 hover:bg-red-800 p-2"
                aria-label="Toggle Menu"
              >
                {isMobileMenuOpen ? <FaTimes className="text-lg" /> : <FaBars className="text-lg" />}
              </motion.button>
            </div>
          </div>
          
          {/* Menu mobile yang muncul saat tombol burger diklik */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div 
                className="md:hidden bg-red-700 pixel-border mb-4"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="p-3 space-y-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleNavigation('/')}
                    className="w-full flex items-center justify-center pixel-button bg-red-600 hover:bg-red-800 px-3 py-2 text-sm"
                  >
                    <FaHome className="mr-2" />
                    <span>Kembali ke Beranda</span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Error message */}
          {/* {error && (
            <div className="mb-3 p-3 bg-yellow-900 text-yellow-200 pixel-border flex items-center">
              <FaExclamationTriangle className="mr-2" />
              <span>{error} Menggunakan data contoh.</span>
            </div>
          )} */}
          
          {/* Info jumlah game */}
          <div className="text-sm text-gray-300 flex items-center justify-center md:justify-start">
            <FaGamepad className="mr-2" />
            {games.length} games ditemukan 
          </div>
        </div>
      </header>

      {/* Konten Utama */}
      <main className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <p className="text-gray-400 max-w-2xl mx-auto">
            Koleksi game Steam saya, diurutkan berdasarkan total waktu bermain.
          </p>
        </motion.div>

        {/* Game Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {games.map((game) => (
            <motion.div 
              key={game.appid}
              className="bg-gray-800 pixel-border overflow-hidden hover:shadow-lg transition-shadow duration-300 transform hover:-translate-y-1"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="relative">
                <img 
                  src={game.header_image} 
                  alt={game.name} 
                  className="w-full h-48 object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = `https://cdn.akamai.steamstatic.com/steam/apps/${game.appid}/header.jpg`;
                  }}
                />
              </div>
              
              <div className="p-4">
                <h3 className="text-base font-bold mb-3 pixel-font line-clamp-2" title={game.name}>
                  {game.name}
                </h3>
                
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center text-sm text-gray-400">
                    <FaClock className="mr-2 flex-shrink-0" />
                    <span className="truncate">Dimainkan: {formatPlaytime(game.playtime_forever)}</span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-400">
                    <FaGamepad className="mr-2 flex-shrink-0" />
                    <span className="truncate">Terakhir: {formatLastPlayed(game.rtime_last_played)}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {games.length === 0 && (
          <motion.div 
            className="text-center py-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <p className="text-gray-400">Tidak ada game yang ditemukan.</p>
          </motion.div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default SteamTierListPage;